import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { useLocation, useHistory, useParams } from "react-router-dom";
import styled from "styled-components";
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

import { Layout } from '~/components/UI';
import Drawer from "./Drawer";
import SummaryDrawer from "./Summary/SummaryDrawer";
import * as actions from "./actions";
import { Header } from "./Header";
import { LeadColumns } from "./LeadColumns";
import { LeadsTable } from "./LeadsTable";
import Summary from "./Summary";
import CadenceSelection from "./CadenceSelection";
import { useLeadsFilter } from "./hooks/useLeadsFilter";
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';
import RestoredFiltersNotice from '~/_shared/components/RestoredFiltersNotice';
import {
  PROSPECTING_FILTERS_KEY,
  getDefaultProspectingFilters,
  getProspectingLeadsRequest,
  getProspectingRestoredSummary,
} from "./filters";

moment.updateLocale("pt-br", {
  weekdaysMin: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
});

// Synchronized scroll container
const SynchronizedScrollContainer = styled.div`
  overflow-x: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }
`;

const FunnelContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: max-content;
`;

const NavigationButton = styled.button`
  position: absolute;
  top: -15px;
  z-index: 1000;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  background: ${props => props.disabled ? '#d9d9d9' : '#1890ff'};
  border: none;
  color: white;
  font-size: 10px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.disabled ? '#d9d9d9' : '#40a9ff'};
    transform: ${props => props.disabled ? 'none' : 'scale(1.1)'};
  }
  
  &:active {
    transform: ${props => props.disabled ? 'none' : 'scale(0.95)'};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LeftButton = styled(NavigationButton)`
  left: 5px;
`;

const RightButton = styled(NavigationButton)`
  right: 5px;
`;

const Leads = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const params = useParams();
  const scrollContainerRef = useRef(null);

  const columns = useSelector((state) => state.prospecting.columns);
  const isLoading = useSelector((state) => state.prospecting.isLoading);
  const user = useSelector((state) => state.user);
  const selectedMonth = useSelector((state) => state.prospecting.selectedMonth);
  const allCadences = useSelector(state => state.settings['prospecting.cadence']);
  const summaryDrawer = useSelector((state) => state.prospecting.summaryDrawer);

  // Period and search survive a refresh; cadence and display mode stay in the URL.
  const persisted = usePersistedFilters(PROSPECTING_FILTERS_KEY, getDefaultProspectingFilters);
  const filters = persisted.filters;
  const setFilters = persisted.setFilters;
  const date = filters.date;
  const dateMode = filters.dateMode;
  const searchQuery = filters.search;
  const [selectedCadence, setSelectedCadence] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Only show debug logs for prospecting module
  const isProspectingModule = location.pathname.includes('/prospeccao');

  // Read cadence from URL path parameter on mount and when URL changes
  useEffect(function() {
    var cadenceIdFromUrl = params.cadenceId;
    if (cadenceIdFromUrl) {
      var parsedId = parseInt(cadenceIdFromUrl, 10);
      if (!isNaN(parsedId) && parsedId !== selectedCadence) {
        setSelectedCadence(parsedId);
      }
    } else if (selectedCadence !== null && !location.pathname.includes('/table')) {
      // URL has no cadence but state has one - clear it (user navigated back)
      // But only if not in table view
      setSelectedCadence(null);
    }
  }, [params.cadenceId]);

  // Update URL when cadence is selected
  var handleCadenceSelect = useCallback(function(cadenceId) {
    setSelectedCadence(cadenceId);
    if (cadenceId !== null) {
      history.push('/crm/prospeccao/' + cadenceId);
    } else {
      history.push('/crm/prospeccao');
    }
  }, [history]);

  const displayMode = useMemo(() => 
    location.pathname.includes("/prospeccao/table") ? "table" : "funnel",
    [location.pathname]
  );

  // No longer auto-select first cadence - let user choose from landing page

  // Get selected cadence data
  const selectedCadenceData = useMemo(() => {
    if (!allCadences) return null;
    
    // If no cadence is selected, use the first one (default)
    const cadenceId = selectedCadence !== null ? selectedCadence : (allCadences[0] && allCadences[0].id || null);
    if (!cadenceId) return null;
    
    const cadence = allCadences.find(c => c.id === cadenceId);
    if (!cadence) return null;
    
    try {
      const parsedValue = JSON.parse(cadence.value);
      return {
        name: parsedValue.name,
        data: parsedValue.data
      };
    } catch (e) {
      return null;
    }
  }, [allCadences, selectedCadence, isProspectingModule]);

  // Update columns when cadence changes
  useEffect(() => {
    if (selectedCadenceData) {
      dispatch(actions.updateColumnsForCadence(selectedCadenceData));
    }
  }, [selectedCadenceData, dispatch]);

  const handleDisplayModeChange = useCallback((mode) => {
    const newPath = mode === "table" ? "/crm/prospeccao/table" : "/crm/prospeccao";
    history.push(newPath);
  }, [history]);

  // Only updates the filter: the [date] effects below sync redux and load the leads.
  const handleDateChange = useCallback((newDate) => {
    setFilters({ date: newDate });
  }, [setFilters]);

  const handleDateModeChange = useCallback((newMode) => {
    setFilters({ dateMode: newMode });
  }, [setFilters]);

  const handleSearchChange = useCallback((value) => {
    setFilters({ search: value });
  }, [setFilters]);

  const showDrawer = useCallback((data) => {
    dispatch(actions.show(data));
  }, [dispatch]);
  
  const loadMoreLeads = useCallback(({ columnId, currentCount, moreCount }) => {
    dispatch(actions.loadMoreLeads({ columnId, currentCount, moreCount }));
  }, [dispatch]);
  
  const moveLead = useCallback((...props) => {
    dispatch(actions.move(props));
  }, [dispatch]);

  // Sync the (restored) date to Redux on mount and on every change, so Summary,
  // the table and the client-side filter use the same date. Also re-syncs when
  // the IDB cache restore (AppContext, async, lands after mount) brings back
  // the selectedMonth of another visit.
  useEffect(() => {
    if (selectedMonth !== date) {
      dispatch(actions.setDate(date));
    }
  }, [dispatch, date, selectedMonth]);

  // Only load full lead data when a cadence is selected
  // Landing page uses optimized cadenceSummary endpoint
  // Single place that loads on a date change (also the mount load, with the restored date).
  useEffect(() => {
    if (selectedCadence !== null) {
      dispatch(actions.loadLeads(getProspectingLeadsRequest({ date: date })));
    }
  }, [dispatch, date, selectedCadence]);

  // Use the filtered data
  const { filteredData, filteredDataToColumns } = useLeadsFilter(columns, searchQuery, selectedCadenceData, selectedMonth);

  var showSummaryDrawer = useCallback(function(summaryData) {
    // Open drawer with loading state
    dispatch(actions.showSummaryDrawer(summaryData));

    // Determine card_type for the API call
    var cardType = null;
    var useDate = true; // Whether to filter by date
    if (summaryData.columnId === 1) {
      cardType = 'leads';
      // Leads card shows leads created on selected date (filtered by created_at)
    } else if (summaryData.columnId === 4) {
      cardType = 'positivo';
    } else if (summaryData.columnId === 5) {
      cardType = 'negativo';
    } else if (summaryData.columnId && summaryData.columnId.toString().startsWith('step_')) {
      cardType = summaryData.columnId.toString();
    }

    // Fetch leads from backend
    if (cardType) {
      var cadenceName = selectedCadenceData ? selectedCadenceData.name : null;
      var dateParam = useDate ? selectedMonth : null;
      dispatch(actions.loadSummaryCardLeads(cardType, cadenceName, dateParam));
    }
  }, [dispatch, selectedCadenceData, selectedMonth]);

  const closeSummaryDrawer = useCallback(() => {
    dispatch(actions.closeSummaryDrawer());
  }, [dispatch]);

  // Navigation scroll functions
  const scrollLeft = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  }, []);

  const scrollRight = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  }, []);

  // Check scroll position to enable/disable buttons
  const checkScrollButtons = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  // Add scroll event listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkScrollButtons);
      checkScrollButtons(); // Check initial state
      
      return () => {
        scrollContainer.removeEventListener('scroll', checkScrollButtons);
      };
    }
  }, [checkScrollButtons, columns]);

  // Show landing page if no cadence selected
  if (selectedCadence === null) {
    return (
      <Layout>
        <Header
          date={date}
          setDate={handleDateChange}
          dateMode={dateMode}
          setDateMode={handleDateModeChange}
          displayMode={displayMode}
          setDisplayMode={handleDisplayModeChange}
          user={user}
          showDrawer={showDrawer}
          searchQuery={searchQuery}
          setSearchQuery={handleSearchChange}
          selectedCadence={selectedCadence}
          setSelectedCadence={handleCadenceSelect}
          defaultFilters={persisted.defaults}
          onClearFilters={persisted.resetFilters}
        />
        <CadenceSelection onSelect={handleCadenceSelect} />
      </Layout>
    );
  }

  return (
    <Layout>
      <Header
        date={date}
        setDate={handleDateChange}
        dateMode={dateMode}
        setDateMode={handleDateModeChange}
        displayMode={displayMode}
        setDisplayMode={handleDisplayModeChange}
        user={user}
        showDrawer={showDrawer}
        searchQuery={searchQuery}
        setSearchQuery={handleSearchChange}
        selectedCadence={selectedCadence}
        setSelectedCadence={handleCadenceSelect}
        defaultFilters={persisted.defaults}
        onClearFilters={persisted.resetFilters}
      />
      <RestoredFiltersNotice
        visible={persisted.wasRestored}
        summary={getProspectingRestoredSummary(filters, persisted.defaults)}
        onReset={persisted.resetFilters}
        onDismiss={persisted.dismissRestored}
      />
      {displayMode === "funnel" && (
        <div style={{ position: 'relative', marginTop: 16 }}>
          <LeftButton onClick={scrollLeft} disabled={!canScrollLeft}>
            <FaChevronLeft />
          </LeftButton>
          <RightButton onClick={scrollRight} disabled={!canScrollRight}>
            <FaChevronRight />
          </RightButton>
          <SynchronizedScrollContainer ref={scrollContainerRef}>
            <FunnelContent>
              <Summary
                selectedCadence={selectedCadence}
                selectedCadenceData={selectedCadenceData}
                onSummaryCardClick={showSummaryDrawer}
                selectedDate={date}
              />
              <LeadColumns
                columns={filteredDataToColumns}
                moveLead={moveLead}
                date={date}
                isLoading={isLoading}
                showDrawer={showDrawer}
                loadMoreLeads={loadMoreLeads}
                user={user}
              />
            </FunnelContent>
          </SynchronizedScrollContainer>
        </div>
      )}
      {displayMode === "table" && (
        <LeadsTable
          showDrawer={showDrawer}
          data={filteredData}
          isLoading={isLoading}
          columns={columns}
        />
      )}
      <Drawer />
      <SummaryDrawer
        isVisible={summaryDrawer.show}
        onClose={closeSummaryDrawer}
        summaryData={summaryDrawer.summaryData}
        leads={summaryDrawer.leads}
        isLoading={summaryDrawer.isLoading}
        allColumns={columns}
      />
    </Layout>
  );
};

Leads.propTypes = {
  // Add PropTypes here if needed
};

export default React.memo(Leads);
